import React from 'react';
import classNames from 'classnames';
import { Skeleton } from '../layout/loading';

function renderIcon(icon, isPrimary) {
  if (!icon) {
    return null;
  }

  const iconClassName = classNames(
    'h-5 w-5 shrink-0',
    isPrimary ? 'text-white' : 'text-indigo-400'
  );

  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, {
      'aria-hidden': true,
      className: classNames(icon.props.className, iconClassName),
    });
  }

  const Icon = icon;

  return <Icon aria-hidden="true" className={iconClassName} />;
}

export default function Button({
  children,
  disabled = false,
  loading = false,
  saving = false,
  icon,
  iconPosition = 'left',
  className = '',
  isPrimary = false,
  ...props
}) {
  if (loading) {
    return (
      <Skeleton
        className={classNames(
          'rounded-md h-9',
          !className.includes('w-') && 'w-24',
          className
        )}
      />
    );
  }

  const isDisabled = disabled || saving;
  const iconElement = renderIcon(icon, isPrimary);

  const buttonClassNames = classNames(
    'inline-flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm/6 font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 cursor-pointer',
    {
      'opacity-50 cursor-not-allowed disabled:bg-gray-400': isDisabled,
      'bg-indigo-500 hover:bg-indigo-400 text-white focus-visible:outline-indigo-500': isPrimary,
      'bg-white/10 text-white shadow-sm hover:bg-white/20 focus-visible:outline-white/20': !isPrimary
    },
    className
  );

  return (
    <button
      type="button"
      disabled={isDisabled}
      className={buttonClassNames}
      {...props}
    >
      {saving ? (
        <span className="inline-flex items-center gap-2">
          <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          {children}
        </span>
      ) : (
        <>
          {iconPosition !== 'right' && iconElement}
          {children}
          {iconPosition === 'right' && iconElement}
        </>
      )}
    </button>
  );
}
